// src/services/wpApi.js
// WP REST -asiakas (CRA + Vite) + dev-override + selkokieliset virheet.
// Mukana: posts (sivutus, haku, kategoria), categories, single post,
// sekä kuvalle srcSet/sizes -apuri.

function resolveWpBase() {
  // 1) Vite
  try {
    // eslint-disable-next-line no-undef
    if (typeof import.meta !== "undefined" && import.meta.env?.VITE_WP_API_BASE) {
      return import.meta.env.VITE_WP_API_BASE;
    }
  } catch {}

  // 2) CRA
  try {
    if (typeof process !== "undefined" && process.env?.REACT_APP_WP_API_BASE) {
      return process.env.REACT_APP_WP_API_BASE;
    }
  } catch {}

  // 3) DEV-override: localhost → käytä buukkeri.fi:n WP:tä ellei env:iä ole
  if (typeof window !== "undefined" && /localhost|127\.0\.0\.1/.test(window.location.host)) {
    return "https://buukkeri.fi/wp-json/wp/v2";
  }

  // 4) Oletus (kun React renderöidään WP-teemana samassa domainissa)
  return "/wp-json/wp/v2";
}

let WP_BASE = resolveWpBase();
WP_BASE = WP_BASE.replace(/\/+$/, "");
console.log("WP_BASE (resolved) =", WP_BASE);

// --- Yleinen virheystävä fetch ---
async function safeFetchRaw(url) {
  const res = await fetch(url, { redirect: "follow" });
  const txt = await res.text();
  if (!res.ok || /^\s*</.test(txt)) {
    const snippet = txt.slice(0, 220).replace(/\s+/g, " ");
    throw new Error(`WP API virhe ${res.status} @ ${url} → ${snippet}`);
  }
  return { res, txt };
}
async function safeFetch(url) {
  const { txt } = await safeFetchRaw(url);
  return JSON.parse(txt);
}

// --- Media-apurit kuville (srcset/sizes) ---
export function extractFeatured(post) {
  // Palauttaa { url, alt, srcSet, sizes }
  const media =
    post?._embedded?.["wp:featuredmedia"] &&
    post._embedded["wp:featuredmedia"][0];

  const url = media?.source_url || "/fallback-blog.jpg";
  const alt =
    media?.alt_text ||
    (media?.title?.rendered || "").replace(/<[^>]*>/g, "");

  // Rakenna srcSet WP:n kokotiedoista (jos saatavilla)
  const sizesObj = media?.media_details?.sizes || null;
  let srcSet = undefined;
  let sizes = undefined;

  if (sizesObj) {
    // Kerätään yleisimmät koot jos saatavilla (thumbnail, medium, medium_large, large, full)
    const entries = Object.values(sizesObj)
      .filter(Boolean)
      .map((s) => `${s.source_url} ${s.width}w`)
      .sort((a, b) => {
        const aw = Number(a.split(" ").pop()?.replace(/\D/g, "") || 0);
        const bw = Number(b.split(" ").pop()?.replace(/\D/g, "") || 0);
        return aw - bw;
      });

    if (entries.length) {
      srcSet = entries.join(", ");
      // Perusresponsiivinen ohje: 100vw mobiilissa, 50vw ≥1024px
      sizes = "(max-width: 1024px) 100vw, 50vw";
    }
  }

  return { url, alt, srcSet, sizes };
}

// --- Posts listaus: tukee page, perPage, categoryId, search ---
export async function fetchPosts({ page = 1, perPage = 9, categoryId, search } = {}) {
  const params = new URLSearchParams({
    status: "publish",
    _embed: "1",
    page: String(page),
    per_page: String(perPage),
  });
  if (categoryId) params.set("categories", String(categoryId));
  if (search) params.set("search", search);

  const url = `${WP_BASE}/posts?${params.toString()}`;

  // Tarvitaan headerista X-WP-TotalPages → käytä raw-fetchausta
  const { res, txt } = await safeFetchRaw(url);
  const posts = JSON.parse(txt);
  const totalPages = Number(res.headers.get("X-WP-TotalPages") || 1);
  return { posts, totalPages };
}

// --- Yksittäinen post slugilla ---
export async function fetchPostBySlug(slug) {
  const url = `${WP_BASE}/posts?slug=${encodeURIComponent(slug)}&_embed=1`;
  const data = await safeFetch(url);
  if (!Array.isArray(data) || !data.length) throw new Error("Artikkelia ei löytynyt.");
  return data[0];
}

// --- Kategoriat listaan (nimi + id) ---
export async function fetchCategories() {
  // per_page=100 riittää hyvin blogille; tarvittaessa sivuta
  const url = `${WP_BASE}/categories?hide_empty=true&per_page=100`;
  const cats = await safeFetch(url);
  // Palautetaan aakkosissa nimellä
  return cats
    .map((c) => ({ id: c.id, name: c.name, slug: c.slug }))
    .sort((a, b) => a.name.localeCompare(b.name, "fi"));
}
